// © Microsoft Corporation. All rights reserved.

import { MsftStripe, MsftContentCard, MsftContentIndicator, telemetry } from "@fast/components";
import { FluentAnchor, FluentCard, FluentFlipper } from "@fluentui/web-components";
import { html, ref, repeat, when } from "@microsoft/fast-element";
import { PublisherCarousel } from "./PublisherCarousel";
import { PublisherCarouselThemeStyles } from "./PublisherCarousel.styles";
import { PublisherCardData, PublisherCarouselTheme } from "./PublisherCarouselData";
import { String } from "typescript-string-operations";
import { renderHybridTelemetryAsync } from "./PublisherCarousel.telemetry";

FluentAnchor;
FluentCard;
FluentFlipper;
MsftStripe;
MsftContentCard;
MsftContentIndicator;

const aboutSlugGlyph = html<PublisherCarousel>`
    <span slot="details" class="about-slug-glyph" title="${x => x.strings.aboutSlugText}">
        <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg"><path d="M8 7a.5.5 0 01.5.5v3a.5.5 0 01-1 0v-3A.5.5 0 018 7zm0-.75A.749.749 0 108 4.75a.749.749 0 000 1.498zM2 8a6 6 0 1112 0A6 6 0 012 8zm6-5a5 5 0 100 10A5 5 0 008 3z" fill-opacity=".55"/></svg>
    </span>
`;

const carouselTitleTemplate = html<PublisherCarousel>`
    <span class="carousel-title"">
        ${x => x.strings?.morePublisherContentTitle && String.Format(x.strings.morePublisherContentTitle, { publisherName: x.carouselData?.publishers[0]?.name?.toUpperCase() || ""})}
        ${x => aboutSlugGlyph}
    </span>
`;

const carouselSeeAllLinkTemplate = html<PublisherCarousel>`
    <fluent-anchor class="action" appearance="lightweight" slot="action" target="_blank" href="${x => x.carouselData.seeAllLinkUrl}"
        ${telemetry(x => x.telemetryContext?.seeAllLink?.getMetadataTag())}
        ht-id=${x => renderHybridTelemetryAsync(x.telemetryContext?.seeAllLink, true, {anchorParent: true, isNavigate: true})}
    >
        ${x => x.strings.viewProfileLinkText}
    </fluent-anchor>
`;

const imageTemplateForSize = (cardImageSize: { height: number; width: number }, slot = "media") => html<PublisherCardData>`
    ${when(x => x.imageInfo?.sourceImageUrl, html`
        <img
            slot="${slot}"
            height="${cardImageSize.height}"
            width="${cardImageSize.width}"
            src="${x => x.imageInfo?.sourceImageUrl}"
            alt="${x => x.imageInfo?.altText}"
        />
    `)}
    ${when(x => !x.imageInfo?.sourceImageUrl, html`
        <div
            slot="${slot}"
            style="height: ${cardImageSize.height}px; width: ${cardImageSize.width}px"
        >
        </div>
    `)}
`;

const getContentIndicator = (icon: string) => html`
    <msft-content-indicator slot="content-indicator" style="--corner-radius: 4">
        ${icon}
    </msft-content-indicator>
`;

const subscriptionBadgeTemplate = html<PublisherCardData>`
    <div slot="attribution" class="subscription-badge-container">
        <label class="subscription-badge${x => x.publisher.isSubscribed ? " subscribed" : ""}">
        ${x => x.publisher.isSubscribed ? x.localizedStrings?.badgeLabelSubscribed : x.localizedStrings?.badgeLabelNotSubscribed}
        </label>
    </div>
`;

const cardTemplate = (theme: PublisherCarouselTheme) => html<PublisherCardData>`
    <fluent-card class="publisher-card">
        ${x => PublisherCarouselThemeStyles[theme]?.card && html`
            <style> ${PublisherCarouselThemeStyles[theme]?.card.styles.toString()} </style>
        `}
        <msft-content-card
            href="${x => x.clickUrl}"
            target="_blank"
            :anchorTelemetryTag=${x => x.telemetry?.destination?.getMetadataTag()}
            ht-id=${x => renderHybridTelemetryAsync(x.telemetry?.destination, true, {anchorParent: true, isNavigate: true})}
        >
            ${x => x.contentIndicator && getContentIndicator(x.contentIndicator)}
            ${() => imageTemplateForSize(PublisherCarouselThemeStyles[theme]?.imageSize)}
            ${when(x => x.isPremium, subscriptionBadgeTemplate)}
            ${x => x.title}
        </msft-content-card>
    </fluent-card>
`;

const defaultTemplate = () => html<PublisherCarousel>`
    ${when(x => !x.backgroundSheetDisabled, html`<div class="background-sheet"></div>`)}
    <msft-stripe
        ${ref("stripe")}
        speed="4000"
        ${telemetry(x => x.telemetryContext?.carousel?.getMetadataTag())}
        ht-id=${x => renderHybridTelemetryAsync(x.telemetryContext?.carousel, false)}
    >
        ${x => PublisherCarouselThemeStyles[x.theme]?.carousel && html`
            <style> ${PublisherCarouselThemeStyles[x.theme]?.carousel.styles.toString()} </style>
        `}
        <div slot="heading">
            ${when(x => !x.headerDisabled, carouselTitleTemplate)}
        </div>
        <div slot="previous-flipper">
            <fluent-flipper direction="previous" ${telemetry(x => x.telemetryContext?.previousSlideArrow?.getMetadataTag())} ht-id=${x => renderHybridTelemetryAsync(x.telemetryContext?.previousSlideArrow, true)}></fluent-flipper>
        </div>
        <div slot="next-flipper">
            <fluent-flipper
                ${telemetry(x => x.telemetryContext?.nextSlideArrow?.getMetadataTag())}
                ht-id=${x => renderHybridTelemetryAsync(x.telemetryContext?.nextSlideArrow, true)}
                @click=${x => renderHybridTelemetryAsync(x.telemetryContext?.nextSlideArrow, true)}
            ></fluent-flipper>
        </div>
        ${when(x => !x.headerDisabled && x.carouselData?.seeAllLinkUrl, carouselSeeAllLinkTemplate)}
        ${repeat(x => x.carouselData?.cards, x => cardTemplate(x.theme))}
    </msft-stripe>
`;

/** PublisherCarousel webcomponent template structure  */
export const PublisherCarouselTemplate = html<PublisherCarousel>`
    ${when(x => !x.shouldPreventRender && x.carouselData?.cards?.length > 0, defaultTemplate())}
`;